/**
 * Защита незавершённых изменений при переходе в другой раздел (E1.4).
 *
 * Если зарегистрированный редактор изменён (stores/workspaceDrafts), переход на другую
 * страницу ждёт выбора: сохранить, не сохранять или остаться. Неудачное сохранение
 * оставляет пользователя на месте с изменённой формой. Смена параметров той же
 * страницы переходом не считается. Закрытие вкладки с изменениями браузер подтверждает
 * сам. Требует data router (RouterProvider): useBlocker работает только в нём.
 */

import { useEffect, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router';
import { discardDirtyDrafts, hasDirtyDrafts, saveDirtyDrafts } from '@/stores/workspaceDrafts';

export function DraftNavigationGuard() {
	const { t } = useTranslation('common');
	const [saving, setSaving] = useState(false);
	const [saveFailed, setSaveFailed] = useState(false);
	const blocker = useBlocker(
		({ currentLocation, nextLocation }) =>
			currentLocation.pathname !== nextLocation.pathname && hasDirtyDrafts(),
	);

	useEffect(() => {
		const confirmUnload = (event: BeforeUnloadEvent) => {
			if (hasDirtyDrafts()) {
				event.preventDefault();
			}
		};
		window.addEventListener('beforeunload', confirmUnload);
		return () => window.removeEventListener('beforeunload', confirmUnload);
	}, []);

	if (blocker.state !== 'blocked') {
		return null;
	}

	const stay = () => {
		setSaveFailed(false);
		blocker.reset();
	};

	const save = async () => {
		setSaving(true);
		setSaveFailed(false);
		const saved = await saveDirtyDrafts();
		setSaving(false);
		if (saved) {
			blocker.proceed();
		} else {
			setSaveFailed(true);
		}
	};

	return (
		<DialogPrimitive.Root open onOpenChange={(open) => !open && stay()}>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className="ws-workspace-dialog-backdrop fixed inset-0 z-[var(--z-modal)]" />
				<DialogPrimitive.Content className="ws-workspace-dialog fixed top-1/2 left-1/2 z-[var(--z-modal)] -translate-x-1/2 -translate-y-1/2 overflow-auto bg-[var(--ws-surface)]">
					<div className="ws-workspace-dialog-head">
						<DialogPrimitive.Title asChild>
							<h2>{t('drafts.title')}</h2>
						</DialogPrimitive.Title>
					</div>
					<div className="ws-workspace-dialog-body">
						<DialogPrimitive.Description className="ws-muted">
							{t('drafts.description')}
						</DialogPrimitive.Description>
						{saveFailed && (
							<p role="alert" className="ws-field-hint text-[var(--ws-red)]">
								{t('drafts.saveFailed')}
							</p>
						)}
						<div className="ws-heading-actions">
							<button type="button" className="ws-btn ws-subtle" onClick={stay}>
								{t('drafts.stay')}
							</button>
							<button
								type="button"
								className="ws-btn"
								onClick={() => {
									discardDirtyDrafts();
									blocker.proceed();
								}}
							>
								{t('drafts.discard')}
							</button>
							<button type="button" className="ws-btn ws-primary" disabled={saving} onClick={save}>
								{t('drafts.save')}
							</button>
						</div>
					</div>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}
